import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { InboxCounts, ThreadTypeFilter } from 'types/interfaces/thread';
import { THREAD_TYPE_FILTERS, THREAD_TYPE_STYLES } from '../threadTypeStyles';
import './TypeFilterPills.css';

interface Props {
  value: ThreadTypeFilter;
  onChange: (value: ThreadTypeFilter) => void;
  /** Unread counts per type; a badge shows only when its count is above zero. */
  counts?: InboxCounts;
}

/**
 * The type filter above the thread list (Decision 6): All, Trades, Offers,
 * Cases, Direct, System. Filtering is server-side; this only sets the param.
 * One row that scrolls sideways when the list is narrow, with a right-edge
 * fade while more pills are off to the right.
 */
const TypeFilterPills: React.FC<Props> = ({ value, onChange, counts }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState(false);

  const measure = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    setHasMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 1);
  }, []);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    if (typeof ResizeObserver === 'undefined') {
      return () => el.removeEventListener('scroll', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [measure]);

  // Badges appear and disappear with the counts; re-measure when they change.
  useEffect(() => {
    measure();
  }, [counts, measure]);

  return (
    <div className={`thread-type-pills-wrap${hasMore ? ' has-more' : ''}`}>
      <div ref={rowRef} className="thread-type-pills" role="tablist" aria-label="Thread type">
        {THREAD_TYPE_FILTERS.map((filter) => {
          const count = counts ? counts[filter.value] : 0;
          const active = value === filter.value;
          const accent = filter.subjectType ? THREAD_TYPE_STYLES[filter.subjectType].color : undefined;
          return (
            <button
              key={filter.value}
              type="button"
              role="tab"
              aria-selected={active}
              className={`thread-type-pill${active ? ' active' : ''}`}
              style={active && accent ? { borderColor: accent, color: accent } : undefined}
              onClick={() => onChange(filter.value)}
            >
              <span>{filter.label}</span>
              {count > 0 && <span className="thread-type-pill-badge">{count > 99 ? '99+' : count}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TypeFilterPills;
